import React, { FC, useEffect, useState, useMemo } from "react";
// libs
import NextImage from "next/image";
import Slider from "react-slick";
import Mapbox from "src/components/ui/Map";
// components
import Button from "src/components/ui/button";
import ReactRatingComponent from "src/components/ui/Rating";
import ReviewCard from "src/components/ui/Cards/ReviewCard";
import Link from "src/components/ui/Link";
import AppointmentBookWidget from "src/components/ui/AppointmentWidget/Book";
import AppointmentRequestClaimedWidget from "src/components/ui/AppointmentWidget/Request/Claimed";
import AppointmentRequestUnclaimedWidget from "src/components/ui/AppointmentWidget/Request/Unclaimed";
import ClaimYourClinicForm from "src/components/ui/Modals/claimYourClinic/index";
import LightboxModal from "src/components/ui/Modals/Lightbox";
// hooks
import { useWindowSize, useAppDispatch, useAppSelector } from "src/hooks";
import { useRouter } from "next/router";
import { useModal } from "src/hooks";
// redux
import { setReviewClinic } from "src/redux/slices/review";
import { pathClinicsSelector } from "src/redux/slices";
// const
import { EVariants } from "src/constants/variants";
import { LIGHTBOX_MODAL } from "src/constants/modals";
// theme
import { colors } from "src/theme";
import {
  AboutText,
  AppointmentWidgetStickyWrapperWeb,
  AppointmentWidgetWrapperMobile,
  AppointmentWidgetWrapperWeb,
  BackButtonImageWrapper,
  BackButtonText,
  BackButtonWrapper,
  ContentContainer,
  LinksContainer,
  LinksItemImageWrapper,
  LinksItemText,
  LinksItemWrapper,
  MapContainer,
  OpenTimesContainer,
  OpenTimesTitle,
  OpenTimesText,
  TopReviewsContainer,
  TopReviewsItemContainer,
  TopReviewsTitle,
  TopReviewsTextBlue,
  TopReviewsStarsWrapper,
  SectionTitle,
  ServiceImageWrapper,
  ServicesContainer,
  ServiceText,
  ServiceWrapper,
  LogoWrapper,
  SpeciesButton,
  SpeciesList,
  Subtitle,
  TeamAvatar,
  TeamContainer,
  TeamName,
  TeamPosition,
  TeamUserWrapper,
  Wrapper,
  BottomReviewsContainer,
  BottomReviewsImageWrapper,
  BottomReviewsLink,
  BottomReviewsTitle,
  BottomReviewsSubtitle,
  BottomReviewsButtonsWrapper,
  BottomReviewsBox,
  TopReviewsBox,
  TopReviewsItemBox,
  ButtonBox,
  TopReviewsText,
  styles,
  ReviewsContent,
} from "src/components/containers/clinic-details/clinic-details.styles";
// utils
import { getBoundsByCenter } from "src/utils/map";
import { transformRateCountReviews } from "src/utils/reviews";
import { avatarSymbols, generatingColor } from "src/utils/avatar";
import generateSliderSettings from "src/utils/gallerySlider";
import formatedOpenTime from "src/utils/formatedOpenTime";
// types
import { IClinicDetailsProps } from "src/components/containers/clinic-details/clinic-details.types";
import { IMapProps } from "src/components/ui/Map/map.types";
import { amplitudeEvent, formatEventName } from "src/libs/amplitude";
import { LargeTitle } from "src/theme/typography";

const WEEK_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const TABLET = 768;

const ClinicDetailsContainer: FC<IClinicDetailsProps> = ({
  clinic,
  reviews,
  reviewsCount,
  avgRating,
  reviewsStatistic,
}) => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const size = useWindowSize();
  const { openModal } = useModal();
  const { path } = useAppSelector(pathClinicsSelector);
  const settings = generateSliderSettings(size);

  const [containerProps, setContainerProps] = useState<IMapProps["containerProps"] | null>(null);
  const [claimClinic, setClaimClinic] = useState<boolean>(false);

  const backgroundColor = useMemo(generatingColor, []);

  useEffect(() => {
    const center = {
      lng: Number(clinic?.geo_data?.data?.lng),
      lat: Number(clinic?.geo_data?.data?.lat),
    };

    if (Number.isNaN(center.lng) || Number.isNaN(center.lat)) return;

    const bounds = getBoundsByCenter(center, 13);

    if (bounds) {
      setContainerProps({
        clinics: [
          {
            id: clinic.id,
            name: clinic.name,
            slug: clinic.slug,
            address: clinic.address,
            original_logo_url: clinic.original_logo_url,
            logo_url: clinic.logo_url,
            data: clinic.geo_data.data,
            rating: reviewsStatistic.rating,
          },
        ],
        showSearchAsMoveCheckbox: false,
        ne: [bounds.ne.lng, bounds.ne.lat],
        sw: [bounds.sw.lng, bounds.sw.lat],
      });
    }
  }, []);

  const hadleClaimClinic = () => {
    !claimClinic &&
      amplitudeEvent(formatEventName("clinic_details", "side_panel", "claim_listing"));
    setClaimClinic(!claimClinic);
  };

  const galleryArray = () => {
    let gallery;

    if (clinic.gallery.length && clinic.logo_url) {
      gallery = clinic.gallery.map((el) => el.url);
      gallery.push(clinic.logo_url);
    }
    if (clinic.gallery.length && !clinic.logo_url) {
      gallery = clinic.gallery.map((el) => el.url);
    }
    if (!clinic.gallery.length && clinic.logo_url) {
      gallery = [clinic.logo_url];
    }

    return gallery;
  };

  const openImagesGalleryModal = (index: number) => {
    openModal(LIGHTBOX_MODAL, { images: galleryArray(), index });
  };

  const getServicesContainerProps = (length: number) => {
    let columns = 2;

    if (size.width > 768 && size.width < 1200) {
      columns = 3;
    } else if (size.width >= 1200) {
      columns = 4;
    }

    return {
      rows: Math.ceil(length / columns),
      columns,
    };
  };

  const leaveReview = (section: string) => {
    amplitudeEvent(formatEventName("clinic_details", section, "leave_review"));
    dispatch(setReviewClinic(clinic));
    router.push({ pathname: "/leave-review", query: { clinic: clinic?.slug, id: clinic?.id } });
  };

  const getBackLinkPath = () => {
    if (path) {
      return path;
    }

    if (!clinic.geo_data) {
      return "/";
    }

    if (typeof clinic.geo_data?.state === "string") {
      const stateName = clinic.geo_data.state.replaceAll(" ", "-");

      if (clinic.geo_data?.town) {
        const townName = clinic.geo_data.town.replaceAll(" ", "-");

        return `/veterinarian/${stateName}/${townName}?_start=0`;
      }

      if (clinic.geo_data?.city) {
        const cityName = clinic.geo_data.city.replaceAll(" ", "-");

        return `/veterinarian/${stateName}/${cityName}?_start=0`;
      }
    }

    return "/";
  };

  return (
    <Wrapper>
      <ContentContainer>
        <BackButtonWrapper
          onClick={() => {
            amplitudeEvent(formatEventName("clinic_details", "top", "back"));
          }}
        >
          <Link href={getBackLinkPath()} anchorProps={{ style: styles.back }}>
            <BackButtonImageWrapper>
              <NextImage src="/icons/arrow-left-black.svg" width={13.5} height={13.5} />
            </BackButtonImageWrapper>
            <BackButtonText>
              {path
                ? "Back"
                : clinic.geo_data && clinic.geo_data.city
                ? `All clinics in ${clinic.geo_data.city}`
                : clinic.geo_data && clinic.geo_data.town
                ? `All clinics in ${clinic.geo_data.town}`
                : "Back"}
            </BackButtonText>
          </Link>
        </BackButtonWrapper>

        <LargeTitle as="h1">{clinic.name}</LargeTitle>
        <Subtitle>{clinic.address}</Subtitle>
        {!!clinic.original_logo_url || !!clinic.gallery.length ? (
          <ReviewsContent>
            <Slider {...settings} centerMode={false}>
              {galleryArray()?.map((el, index) => (
                <LogoWrapper key={el}>
                  <NextImage
                    onClick={() => openImagesGalleryModal(index)}
                    src={el}
                    layout="fill"
                    objectFit="contain"
                    className="clinic_images"
                    loader={({ src, width, quality }) => src}
                  />
                </LogoWrapper>
              ))}
            </Slider>
          </ReviewsContent>
        ) : null}

        <LightboxModal />

        {!!reviews.length && (
          <TopReviewsContainer>
            <TopReviewsItemContainer>
              <TopReviewsTitle>
                <span style={{ color: colors.grey, marginRight: 5 }}>{avgRating}</span>(
                {transformRateCountReviews(reviewsCount)} review{reviewsCount > 1 && "s"})
              </TopReviewsTitle>
              <TopReviewsStarsWrapper>
                <ReactRatingComponent readonly initialRating={Math.round(+avgRating)} />
                <Button variant={EVariants.SECONDARY} onClick={() => leaveReview("top")}>
                  Leave a review
                </Button>
              </TopReviewsStarsWrapper>
            </TopReviewsItemContainer>
            {clinic.pets_registered && (
              <TopReviewsItemContainer>
                <TopReviewsTitle isMarginBtm>Pets registered</TopReviewsTitle>
                <TopReviewsTextBlue>{clinic.pets_registered}</TopReviewsTextBlue>
              </TopReviewsItemContainer>
            )}
            {clinic.response_time && (
              <TopReviewsItemContainer>
                <TopReviewsTitle isMarginBtm>Response time</TopReviewsTitle>
                <TopReviewsTextBlue>{clinic.response_time} mins</TopReviewsTextBlue>
              </TopReviewsItemContainer>
            )}
          </TopReviewsContainer>
        )}
        {!reviews.length && (
          <TopReviewsContainer>
            <TopReviewsItemContainer>
              <TopReviewsTitle>
                <span style={{ color: colors.greyDark }}>No reviews yet</span>
              </TopReviewsTitle>
              <TopReviewsStarsWrapper>
                <ReactRatingComponent readonly initialRating={0} />
                <Button variant={EVariants.SECONDARY} onClick={() => leaveReview("top")}>
                  Leave a review
                </Button>
              </TopReviewsStarsWrapper>
            </TopReviewsItemContainer>
            {clinic.pets_registered && (
              <TopReviewsItemContainer>
                <TopReviewsTitle isMarginBtm>Pets registered</TopReviewsTitle>
                <TopReviewsTextBlue>{clinic.pets_registered}</TopReviewsTextBlue>
              </TopReviewsItemContainer>
            )}
            {clinic.response_time && (
              <TopReviewsItemContainer>
                <TopReviewsTitle isMarginBtm>Response time</TopReviewsTitle>
                <TopReviewsTextBlue>{clinic.response_time} mins</TopReviewsTextBlue>
              </TopReviewsItemContainer>
            )}
          </TopReviewsContainer>
        )}

        {/* {!!clinic.from_petsapp && (
          <SpeciesList>
            <SpeciesButton>Recommended</SpeciesButton>
            <SpeciesItem offsetFirst={true}>Small pets</SpeciesItem>
            <SpeciesItem offsetFirst={true}>Exotics</SpeciesItem>
            <SpeciesItem offsetFirst={true}>Open 24/7</SpeciesItem>
          </SpeciesList>
        )} */}

        {!!clinic.from_petsapp && (
          <SpeciesList>
            {clinic.from_petsapp && <SpeciesButton offsetFirst={false}>Recommended</SpeciesButton>}
            {/* {clinic?.services?.slice(0, 4).map((item: any) => (
              <SpeciesItem key={item.id}>{item.name}</SpeciesItem>
            ))} */}
          </SpeciesList>
        )}

        {clinic.request_an_appointment === true && (
          <AppointmentWidgetWrapperMobile>
            {/*<AppointmentBookWidget />*/}
            {/*<AppointmentRequestClaimedWidget pets_id={'test_pets_id'} />*/}
            <AppointmentRequestUnclaimedWidget clinic={clinic} claimClinic={hadleClaimClinic} />
          </AppointmentWidgetWrapperMobile>
        )}

        {clinic.description && (
          <>
            <SectionTitle>About</SectionTitle>
            <AboutText>{clinic.description}</AboutText>
          </>
        )}

        {!!(clinic.services || []).length && (
          <>
            <SectionTitle>Services</SectionTitle>
            <ServicesContainer {...getServicesContainerProps(clinic.services.length)}>
              {clinic.services.map((service) => (
                <ServiceWrapper key={service.id}>
                  <ServiceImageWrapper>
                    <NextImage
                      src={service.icon?.url}
                      width={48}
                      height={48}
                      loader={({ src }) => src}
                    />
                  </ServiceImageWrapper>
                  <ServiceText>{service.name}</ServiceText>
                </ServiceWrapper>
              ))}
            </ServicesContainer>
          </>
        )}
        {/*{!!(clinic.services || []).length && (*/}
        {/*  <>*/}
        {/*    <SectionTitle>Services</SectionTitle>*/}
        {/*    <ServicesContainer>*/}
        {/*      <ServiceWrapper>*/}
        {/*        <ServiceImageWrapper>*/}
        {/*          <NextImage*/}
        {/*            src='/icons/square-camera-blue.svg'*/}
        {/*            width={48}*/}
        {/*            height={48}*/}
        {/*          />*/}
        {/*        </ServiceImageWrapper>*/}
        {/*        <ServiceText>Video consultations</ServiceText>*/}
        {/*      </ServiceWrapper>*/}

        {/*      <ServiceWrapper>*/}
        {/*        <ServiceImageWrapper>*/}
        {/*          <NextImage*/}
        {/*            src='/icons/square-message-blue.svg'*/}
        {/*            width={48}*/}
        {/*            height={48}*/}
        {/*          />*/}
        {/*        </ServiceImageWrapper>*/}
        {/*        <ServiceText>Instant messaging</ServiceText>*/}
        {/*      </ServiceWrapper>*/}

        {/*      <ServiceWrapper>*/}
        {/*        <ServiceImageWrapper>*/}
        {/*          <NextImage*/}
        {/*            src='/icons/square-calendar-blue.svg'*/}
        {/*            width={48}*/}
        {/*            height={48}*/}
        {/*          />*/}
        {/*        </ServiceImageWrapper>*/}
        {/*        <ServiceText>Online appointment booking</ServiceText>*/}
        {/*      </ServiceWrapper>*/}

        {/*      <ServiceWrapper>*/}
        {/*        <ServiceImageWrapper>*/}
        {/*          <NextImage*/}
        {/*            src='/icons/square-smile-grey.svg'*/}
        {/*            width={48}*/}
        {/*            height={48}*/}
        {/*          />*/}
        {/*        </ServiceImageWrapper>*/}
        {/*        <ServiceText>Behaviourists</ServiceText>*/}
        {/*      </ServiceWrapper>*/}

        {/*      <ServiceWrapper>*/}
        {/*        <ServiceImageWrapper>*/}
        {/*          <NextImage*/}
        {/*            src='/icons/square-calendar-grey.svg'*/}
        {/*            width={48}*/}
        {/*            height={48}*/}
        {/*          />*/}
        {/*        </ServiceImageWrapper>*/}
        {/*        <ServiceText>Consultations</ServiceText>*/}
        {/*      </ServiceWrapper>*/}

        {/*      <ServiceWrapper>*/}
        {/*        <ServiceImageWrapper>*/}
        {/*          <NextImage*/}
        {/*            src='/icons/square-bug-grey.svg'*/}
        {/*            width={48}*/}
        {/*            height={48}*/}
        {/*          />*/}
        {/*        </ServiceImageWrapper>*/}
        {/*        <ServiceText>Flea and worm prevention</ServiceText>*/}
        {/*      </ServiceWrapper>*/}

        {/*      <ServiceWrapper>*/}
        {/*        <ServiceImageWrapper>*/}
        {/*          <NextImage*/}
        {/*            src='/icons/square-profile-card-grey.svg'*/}
        {/*            width={48}*/}
        {/*            height={48}*/}
        {/*          />*/}
        {/*        </ServiceImageWrapper>*/}
        {/*        <ServiceText>Membership</ServiceText>*/}
        {/*      </ServiceWrapper>*/}

        {/*      <ServiceWrapper>*/}
        {/*        <ServiceImageWrapper>*/}
        {/*          <NextImage*/}
        {/*            src='/icons/square-scissors-grey.svg'*/}
        {/*            width={48}*/}
        {/*            height={48}*/}
        {/*          />*/}
        {/*        </ServiceImageWrapper>*/}
        {/*        <ServiceText>Neutering and spaying</ServiceText>*/}
        {/*      </ServiceWrapper>*/}

        {/*      <ServiceWrapper>*/}
        {/*        <ServiceImageWrapper>*/}
        {/*          <NextImage*/}
        {/*            src='/icons/square-medic-case-grey.svg'*/}
        {/*            width={48}*/}
        {/*            height={48}*/}
        {/*          />*/}
        {/*        </ServiceImageWrapper>*/}
        {/*        <ServiceText>Nurse clinics</ServiceText>*/}
        {/*      </ServiceWrapper>*/}

        {/*      <ServiceWrapper>*/}
        {/*        <ServiceImageWrapper>*/}
        {/*          <NextImage*/}
        {/*            src='/icons/square-dental-grey.svg'*/}
        {/*            width={48}*/}
        {/*            height={48}*/}
        {/*          />*/}
        {/*        </ServiceImageWrapper>*/}
        {/*        <ServiceText>Oral and dental</ServiceText>*/}
        {/*      </ServiceWrapper>*/}

        {/*      <ServiceWrapper>*/}
        {/*        <ServiceImageWrapper>*/}
        {/*          <NextImage*/}
        {/*            src='/icons/square-passport-grey.svg'*/}
        {/*            width={48}*/}
        {/*            height={48}*/}
        {/*          />*/}
        {/*        </ServiceImageWrapper>*/}
        {/*        <ServiceText>Pet passports</ServiceText>*/}
        {/*      </ServiceWrapper>*/}

        {/*      <ServiceWrapper>*/}
        {/*        <ServiceImageWrapper>*/}
        {/*          <NextImage*/}
        {/*            src='/icons/square-list-plus-grey.svg'*/}
        {/*            width={48}*/}
        {/*            height={48}*/}
        {/*          />*/}
        {/*        </ServiceImageWrapper>*/}
        {/*        <ServiceText>Presecriptions</ServiceText>*/}
        {/*      </ServiceWrapper>*/}

        {/*      <ServiceWrapper>*/}
        {/*        <ServiceImageWrapper>*/}
        {/*          <NextImage*/}
        {/*            src='/icons/square-vaccine-grey.svg'*/}
        {/*            width={48}*/}
        {/*            height={48}*/}
        {/*          />*/}
        {/*        </ServiceImageWrapper>*/}
        {/*        <ServiceText>Vaccinations</ServiceText>*/}
        {/*      </ServiceWrapper>*/}

        {/*      <ServiceWrapper>*/}
        {/*        <ServiceImageWrapper>*/}
        {/*          <NextImage*/}
        {/*            src='/icons/square-tablet-grey.svg'*/}
        {/*            width={48}*/}
        {/*            height={48}*/}
        {/*          />*/}
        {/*        </ServiceImageWrapper>*/}
        {/*        <ServiceText>Pain management</ServiceText>*/}
        {/*      </ServiceWrapper>*/}

        {/*      <ServiceWrapper>*/}
        {/*        <ServiceImageWrapper>*/}
        {/*          <NextImage*/}
        {/*            src='/icons/square-weight-grey.svg'*/}
        {/*            width={48}*/}
        {/*            height={48}*/}
        {/*          />*/}
        {/*        </ServiceImageWrapper>*/}
        {/*        <ServiceText>Weight management</ServiceText>*/}
        {/*      </ServiceWrapper>*/}
        {/*    </ServicesContainer>*/}
        {/*  </>*/}
        {/*)}*/}

        {!!containerProps?.ne && !!containerProps?.sw && (
          <>
            <SectionTitle>Location</SectionTitle>
            <MapContainer>
              <Mapbox containerProps={containerProps} heightImage={{ height: "110px" }} />
            </MapContainer>
          </>
        )}

        {!!clinic.hours_of_operation && !!Object.keys(clinic.hours_of_operation).length && (
          <>
            <SectionTitle>Opening times</SectionTitle>
            <OpenTimesContainer>
              {!!clinic.hours_of_operation.Monday && (
                <div>
                  <OpenTimesTitle>Monday</OpenTimesTitle>
                  <OpenTimesText>
                    {formatedOpenTime(clinic.hours_of_operation.Monday, router.locale)}
                  </OpenTimesText>
                </div>
              )}

              {!!clinic.hours_of_operation.Tuesday && (
                <div>
                  <OpenTimesTitle>Tuesday</OpenTimesTitle>
                  <OpenTimesText>
                    {formatedOpenTime(clinic.hours_of_operation.Tuesday, router.locale)}
                  </OpenTimesText>
                </div>
              )}

              {!!clinic.hours_of_operation.Wednesday && (
                <div>
                  <OpenTimesTitle>Wednesday</OpenTimesTitle>
                  <OpenTimesText>
                    {formatedOpenTime(clinic.hours_of_operation.Wednesday, router.locale)}
                  </OpenTimesText>
                </div>
              )}

              {!!clinic.hours_of_operation.Thursday && (
                <div>
                  <OpenTimesTitle>Thursday</OpenTimesTitle>
                  <OpenTimesText>
                    {formatedOpenTime(clinic.hours_of_operation.Thursday, router.locale)}
                  </OpenTimesText>
                </div>
              )}

              {!!clinic.hours_of_operation.Friday && (
                <div>
                  <OpenTimesTitle>Friday</OpenTimesTitle>
                  <OpenTimesText>
                    {formatedOpenTime(clinic.hours_of_operation.Friday, router.locale)}
                  </OpenTimesText>
                </div>
              )}

              {!!clinic.hours_of_operation.Saturday && (
                <div>
                  <OpenTimesTitle>Saturday</OpenTimesTitle>
                  <OpenTimesText>
                    {formatedOpenTime(clinic.hours_of_operation.Saturday, router.locale)}
                  </OpenTimesText>
                </div>
              )}

              {!!clinic.hours_of_operation.Sunday && (
                <div>
                  <OpenTimesTitle>Sunday</OpenTimesTitle>
                  <OpenTimesText>
                    {formatedOpenTime(clinic.hours_of_operation.Sunday, router.locale)}
                  </OpenTimesText>
                </div>
              )}

              {(Object.entries(clinic.hours_of_operation) || [])
                .filter(([day]) => !WEEK_DAYS.includes(day))
                .map(([day, value]) => (
                  <div key={day}>
                    <OpenTimesTitle>{day}</OpenTimesTitle>
                    <OpenTimesText>{value}</OpenTimesText>
                  </div>
                ))}
            </OpenTimesContainer>
          </>
        )}

        {!!clinic.team.filter((el) => el.full_name).length && (
          <>
            <SectionTitle>Team</SectionTitle>
            <TeamContainer rows={clinic.team.length}>
              {clinic.team.map((item) => (
                <TeamUserWrapper key={item.id}>
                  {item?.image ? (
                    <TeamAvatar>
                      <NextImage
                        width={56}
                        height={56}
                        src={item.image?.url || "/test"}
                        loader={({ src }) => src}
                      />
                    </TeamAvatar>
                  ) : (
                    <TeamAvatar style={{ backgroundColor: backgroundColor }}>
                      {avatarSymbols(item.full_name)}
                    </TeamAvatar>
                  )}
                  <TeamName>{item.full_name || "Name"}</TeamName>
                  <TeamPosition>{item.position || ""}</TeamPosition>
                </TeamUserWrapper>
              ))}
            </TeamContainer>
          </>
        )}

        {!!reviews.length && (
          <>
            <SectionTitle>Reviews</SectionTitle>
            <BottomReviewsContainer>
              <BottomReviewsBox>
                <BottomReviewsImageWrapper>
                  <NextImage
                    src="/icons/star-full-blue.svg"
                    width={27}
                    height={27}
                    layout="fixed"
                  />
                </BottomReviewsImageWrapper>
                <BottomReviewsTitle>{avgRating}</BottomReviewsTitle>
                <BottomReviewsSubtitle>
                  ({transformRateCountReviews(reviewsCount)} review{reviewsCount > 1 && "s"})
                </BottomReviewsSubtitle>
              </BottomReviewsBox>
              <BottomReviewsButtonsWrapper
                onClick={() =>
                  amplitudeEvent(formatEventName("clinic_details", "reviews", "view_all_reviews"))
                }
              >
                <Link href={`/reviews/city/area/${clinic.id}`}>
                  <Button variant={EVariants.SECONDARY}>View all</Button>
                </Link>
                <Button
                  variant={size.width < TABLET ? EVariants.PRIMARY : EVariants.SECONDARY}
                  onClick={() => leaveReview("reviews")}
                >
                  Leave a review
                </Button>
              </BottomReviewsButtonsWrapper>
            </BottomReviewsContainer>
            <BottomReviewsLink>Recent reviews</BottomReviewsLink>
            {reviews.map((review) => (
              <ReviewCard key={review.id} {...review} />
            ))}
          </>
        )}
        {!reviews.length && (
          <>
            <SectionTitle>Reviews</SectionTitle>
            <TopReviewsBox>
              <TopReviewsItemBox>
                <ReactRatingComponent readonly initialRating={0} />
                <TopReviewsText>
                  {size.width < TABLET ? "None yet" : "No reviews yet"}
                </TopReviewsText>
              </TopReviewsItemBox>
              <ButtonBox>
                <Button
                  variant={size.width < TABLET ? EVariants.PRIMARY : EVariants.SECONDARY}
                  style={{ width: "100%" }}
                  onClick={() => leaveReview("reviews")}
                >
                  Leave a review
                </Button>
              </ButtonBox>
            </TopReviewsBox>
          </>
        )}

        <SectionTitle>Links</SectionTitle>
        <LinksContainer>
          {/*{clinic.practices?.chat && (*/}
          {/*  <LinksItemWrapper>*/}
          {/*    <LinksItemImageWrapper>*/}
          {/*      <NextImage*/}
          {/*        src='/icons/square-message-blue.svg'*/}
          {/*        width={48}*/}
          {/*        height={48}*/}
          {/*      />*/}
          {/*    </LinksItemImageWrapper>*/}
          {/*    <LinksItemText>Start chat</LinksItemText>*/}
          {/*  </LinksItemWrapper>*/}
          {/*)}*/}

          {clinic.website && (
            <LinksItemWrapper
              href={clinic.website}
              onClick={() => amplitudeEvent(formatEventName("clinic_details", "links", "website"))}
            >
              <LinksItemImageWrapper>
                <NextImage src="/icons/square-world-blue.svg" width={48} height={48} />
              </LinksItemImageWrapper>
              <LinksItemText>Website</LinksItemText>
            </LinksItemWrapper>
          )}

          {clinic.social?.facebook && (
            <LinksItemWrapper
              href={clinic.social?.facebook}
              onClick={() => amplitudeEvent(formatEventName("clinic_details", "links", "facebook"))}
            >
              <LinksItemImageWrapper>
                <NextImage src="/icons/square-facebook-blue.svg" width={48} height={48} />
              </LinksItemImageWrapper>
              <LinksItemText>Facebook</LinksItemText>
            </LinksItemWrapper>
          )}

          {clinic.phone && (
            <LinksItemWrapper
              href={`tel:${clinic.phone}`}
              onClick={() => amplitudeEvent(formatEventName("clinic_details", "links", "phone"))}
            >
              <LinksItemImageWrapper>
                <NextImage src="/icons/square-phone-blue.svg" width={48} height={48} />
              </LinksItemImageWrapper>
              <LinksItemText>Phone</LinksItemText>
            </LinksItemWrapper>
          )}
        </LinksContainer>
      </ContentContainer>

      <AppointmentWidgetWrapperWeb>
        <AppointmentWidgetStickyWrapperWeb>
          {/*<AppointmentBookWidget />*/}
          {/*<AppointmentRequestClaimedWidget pets_id={'test_pets_id'} />*/}
          <AppointmentRequestUnclaimedWidget clinic={clinic} claimClinic={hadleClaimClinic} />
        </AppointmentWidgetStickyWrapperWeb>
      </AppointmentWidgetWrapperWeb>

      {claimClinic && (
        <ClaimYourClinicForm
          isOpen={claimClinic}
          clinic={clinic}
          handleCloseModal={hadleClaimClinic}
        />
      )}
    </Wrapper>
  );
};

export default ClinicDetailsContainer;
